/**
 * Issue Detail API Hook
 *
 * Lazy-fetches full issue details when a timeline card is expanded.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api-client';
import { CONTAINER_ISSUES_QUERY_KEYS } from './use-container-issues';

// ============================================================================
// Types (matches GET /container-issues/issues/{id} response)
// ============================================================================

export interface IssueDetail {
    issue_id: string;
    container_id: string;
    issue_type: string;
    issue_source: string | null;
    severity: 'critical' | 'high' | 'medium' | 'low';
    priority: string | null;
    impact_areas: string[] | null;
    title: string;
    description: string | null;
    root_cause: string | null;
    recommended_actions: string[] | null;
    blocks_shipment: boolean;
    estimated_delay_hours: number | null;
    financial_impact_usd: number | null;
    risk_contribution: number | null;
    shipment_phase: string | null;
    location: {
        port_code: string;
        name: string | null;
        country_code: string | null;
    } | null;
    temperature: {
        recorded: number | null;
        setpoint: number | null;
        deviation: number | null;
    } | null;
    resolution_status: string;
    resolution_notes: string | null;
    resolved_at: string | null;
    resolution_time_hours: number | null;
    solution_tags: string[] | null;
    reported_at: string | null;
    created_at: string | null;
    updated_at: string | null;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Lazy-fetch issue details. Only fires when `enabled` is true (i.e. card is expanded).
 */
export function useIssueDetail(
    issueId: string | undefined,
    options?: { enabled?: boolean }
) {
    return useQuery({
        queryKey: CONTAINER_ISSUES_QUERY_KEYS.detail(issueId || ''),
        queryFn: async (): Promise<IssueDetail> => {
            return await apiGet<IssueDetail>(
                `/container-issues/issues/${issueId}`
            );
        },
        enabled: (options?.enabled ?? false) && !!issueId,
        staleTime: 60_000,
    });
}
